/* OsCatalogDetailsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./OsCatalogDetailsViewRenderer");

class OsCatalogDetailsViewComponent extends EventEmitter {
    constructor(logger, gameEditor, userSession, MUF, steveIo) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._userSession = userSession;
        this._steveIo = steveIo;

        this._osCatalog = undefined;
        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerOsCatalogListeners() {
        this._osCatalog.on("DirtyStateChanged", (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this)));
        this._osCatalog.on("PendingPatchesChanged", (this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this)));
        this._osCatalog.on("PristinePropertiesChanged", (this._onPendingPatchesChanged = this._onPristinePropertiesChanged.bind(this)));
    }

    _unregisterOsCatalogListeners() {
        this._osCatalog.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._osCatalog.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._osCatalog.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get steveIo() {
        return this._steveIo;
    }

    getOsCatalog() {
        return this._osCatalog;
    }

    get osCatalog() {
        return this.getOsCatalog();
    }

    set osCatalog(newCatalog) {
        this._osCatalog = newCatalog;
        return true;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer
            .shutdown()
            .then(() => {
                if (!_.isUndefined(this._osCatalog)) {
                    this._unregisterOsCatalogListeners();
                } else {
                    return Promise.resolve();
                }
            })
            .then(
                () => {
                    this._osCatalog = undefined;
                    this._steveIo = undefined;
                    this._logger = undefined;
                    this._gameEditor = undefined;
                    this._renderer = undefined;
                },
                (err) => {
                    this._logger.warn(err, "OsCatalogDetailsViewComponent.shutdown() error.");
                }
            );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);

        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive"
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text"
                    }
                })
                .then(() => {
                    // Handle validation and if valid save in the Hidden event handler function
                    this._modalUserFeedback.on("Hidden", (this._onConfirmationModalHidden = this._onConfirmationModalHidden.bind(this)));
                    // Required to resolve the promise with current context
                    return Promise.resolve(this);
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve(this._osCatalog.clearPendingPatches(true))
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                            .then(() => Promise.resolve());
                    } else {
                        this._logger.warn(err, "Error in onBeforeUnloadFunc! Err: ==> OsCatalogDetailsViewComponent");
                    }
                });
        };

        if (this.isDirty()) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged() {
        // TODO - this is a quick hack because the Array ref adapter does a shutdown and complete rebuild every time you call setValue. the effect to the user is they lose focus in the proxy list if they stop typing longer than the debounce
        // this.emit('OsCatalogPropertiesChanged');
    }
    _onPristinePropertiesChanged(pristineProperties, before) {
        this.emit("OsCatalogPropertiesChanged", pristineProperties, before);
    }

    _onConfirmationModalHidden() {
        // Remove the listener on Hidden event first so that it doesnt work for other veil hides
        this._modalUserFeedback.removeListener("Hidden", this._onConfirmationModalHidden);
        // Hidden event handler not required for validators as for one instance only one would fail

        this.emit("ConfirmationModelHidden");
    }
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    isDirty() {
        return this._osCatalog.isDirty();
    }

    get gameEditor() {
        return this.getGameEditor();
    }

    get logger() {
        return this._logger;
    }

    getGameEditor() {
        return this._gameEditor;
    }
    whenModelIsReady() {
        return this.gameEditor
            .whenOsCatalogIsReady()
            .then(() => {
                this._osCatalog = this.gameEditor.getOsCatalog();
                return this._osCatalog;
            })
            .then(() => {
                this._registerOsCatalogListeners();
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(err, "OsCatalogDetailsViewComponent.whenModelIsReady() error");
                this._modalUserFeedback.displayError({
                    errorMsg: "This catalog cannot be opened.",
                    errorCode: ""
                });
                return Promise.reject(err);
            });
    }

    isCatalogFormValid(rollbackInvalidData = false) {
        const catalog = this._osCatalog;
        const inputChanges = catalog.getProperties();
        const oldData = catalog.getPristineProperties();
        const inputDataName = inputChanges.name.trim();
        const isInputNameEmpty = inputDataName.length === 0;

        if (isInputNameEmpty && rollbackInvalidData === false) {
            throw {
                errorMsg: "Name cannot be left empty. Please enter a valid catalog name.",
                errorCode: "SyntaxError",
                title: "Error"
            };
        } else if (isInputNameEmpty && rollbackInvalidData === true) {
            catalog.setName(oldData.name.trim());
            return true;
        } else {
            catalog.setName(inputDataName);
            return true;
        }
    }

    saveCatalog() {
        return Promise.resolve()
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._osCatalog.isDirty()) {
                    this.isCatalogFormValid();
                    this.emit("Dirty", false);
                    this.emit("DisableViewActions");
                    return this._osCatalog.update();
                }
            })
            .catch((err) => {
                this._logger.warn({ error: err }, "OsCatalogDetailsViewComponent.saveCatalog() error.");
                if (err.errorCode === "NoPendingUpdates") {
                    this.emit("OsCatalogPropertiesChanged");
                    return Promise.reject();
                } else {
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }

    resetCatalogProperties() {
        this._osCatalog.clearPendingRelationships(true);
        this._osCatalog.clearPendingPatches();
        return Promise.resolve();
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OsCatalogDetailsViewComponent;

OsCatalogDetailsViewComponent.$inject = ["$teve.logger", "gameEditor.gameEditor", "$teve.userSession", "$teve.ui.userFeedback", "$teve.io"];

OsCatalogDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "OS Catalog Details",
    icon: "osmanager.edit_os_catalog",
    shown: true,
    title: "Details",
    order: 1
};
